import {
  Excalidraw,
  LiveCollaborationTrigger,
  CaptureUpdateAction,
  reconcileElements,
  useEditorInterface,
  ExcalidrawAPIProvider,
  useExcalidrawAPI,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import {
  clearAppStateForLocalStorage,
  getDefaultAppState,
} from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import Trans from "@excalidraw/excalidraw/components/Trans";
import DropdownMenu from "@excalidraw/excalidraw/components/dropdownMenu/DropdownMenu";
import {
  APP_NAME,
  EVENT,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  historyIcon,
  LoadIcon,
  loginIcon,
  MagicIcon,
  pencilIcon,
  PlusIcon,
  save,
  saveAs,
  usersIcon,
  share,
} from "@excalidraw/excalidraw/components/icons";
import { useTunnels } from "@excalidraw/excalidraw/context/tunnels";
import { isElementLink } from "@excalidraw/element";
import {
  bumpElementVersions,
  restoreAppState,
  restoreElements,
  restoreLibraryItems,
} from "@excalidraw/excalidraw/data/restore";
import { duplicateElements, newElementWith } from "@excalidraw/element";
import { convertToExcalidrawElements } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";
import {
  distributeLibraryItemsOnSquareGrid,
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type { ExcalidrawElementSkeleton } from "@excalidraw/element";
import type {
  AppState,
  BinaryFileData,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
  ExcalidrawProps,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import { AuthPage } from "../src/components/Auth/AuthPage";
import { useAuth } from "../src/hooks/useAuth";
import { useBoards } from "../src/hooks/useBoards";
import {
  AIBoardGenerationError,
  generateBoardWithAI,
} from "../src/services/aiBoardGenerationService";
import {
  DRAW_LIBRARY_AI_CATEGORY_OPTIONS,
  DRAW_LIBRARY_CATEGORY_AUTO,
  DRAW_LIBRARY_CATEGORY_ALL,
  DRAW_LIBRARY_MANUAL_CATEGORY_OPTIONS,
  buildLightweightDrawLibraryCatalog,
  getDrawLibraryErrorMessage,
  loadDrawLibraryCatalog,
  loadDrawLibraryFile,
  type DrawLibraryCatalogItem,
  type DrawLibraryCatalogStatus,
  type DrawLibraryInsertMode,
} from "../src/services/drawLibraryService";
import {
  createAIGeneration,
  getAIGenerations,
} from "../src/services/aiGenerationsService";
import {
  generateExcalidrawTemplate,
  type MockAITemplateType,
} from "../src/utils/generateExcalidrawTemplate";
import { normalizeGeneratedExcalidrawElements } from "../src/utils/normalizeGeneratedExcalidrawElements";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  FIREBASE_STORAGE_PREFIXES,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import {
  COLLABORATION_UNAVAILABLE_MESSAGE,
  isCollaborationConfigured,
} from "./collab/collabConfig";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import { TopErrorBoundary } from "./components/TopErrorBoundary";

import {
  exportToBackend,
  getCollaborationLinkData,
  importFromBackend,
  isCollaborationLink,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import { FileStatusStore } from "./data/fileStatusStore";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import {
  AIMockGeneratorDialog,
  type AIGenerationInsertMode,
  type AIGenerationMode,
} from "./components/AIMockGeneratorDialog";
import { DrawLibraryDialog } from "./components/DrawLibraryDialog";

import "./index.scss";

import type { AIGeneration } from "../src/services/aiGenerationsService";
import type { GenerateBoardWithAIResponse } from "../src/services/aiBoardGenerationService";
import type { Board } from "../src/services/boardsService";
import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

const DrawTopMenuActions = ({
  disabled,
  onGenerateAI,
  onOpenLibrary,
}: {
  disabled: boolean;
  onGenerateAI: () => void;
  onOpenLibrary: () => void;
}) => {
  const { TTDDialogTriggerTunnel } = useTunnels();

  return (
    <TTDDialogTriggerTunnel.In>
      <DropdownMenu.Item
        badge={<DropdownMenu.Item.Badge>IA</DropdownMenu.Item.Badge>}
        disabled={disabled}
        icon={MagicIcon}
        onSelect={onGenerateAI}
      >
        Gerar com IA
      </DropdownMenu.Item>
      <DropdownMenu.Item icon={LoadIcon} onSelect={onOpenLibrary}>
        Biblioteca Draw
      </DropdownMenu.Item>
    </TTDDialogTriggerTunnel.In>
  );
};

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  isCollabEnabled: boolean;
  excalidrawAPI: ExcalidrawImperativeAPI;
  userId: string;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage(opts.userId);

  let scene: Omit<
    RestoredDataState,
    // we're not storing files in the scene database/localStorage, and instead
    // fetch them async from a different store
    "files"
  > & {
    scrollToContent?: boolean;
  } = {
    elements: restoreElements(localDataState?.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: restoreAppState(localDataState?.appState, null),
  };

  let roomLinkData = getCollaborationLinkData(window.location.href);
  if (roomLinkData && !opts.isCollabEnabled) {
    roomLinkData = null;
    window.history.replaceState({}, APP_NAME, window.location.origin);
    scene.appState = {
      ...scene.appState,
      errorMessage: COLLABORATION_UNAVAILABLE_MESSAGE,
    };
  }

  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        const imported = await importFromBackend(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
        );

        scene = {
          elements: bumpElementVersions(
            restoreElements(imported.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            localDataState?.elements,
          ),
          appState: restoreAppState(
            imported.appState,
            // local appState when importing from backend to ensure we restore
            // localStorage user settings which we do not persist on server.
            localDataState?.appState,
          ),
        };
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

type SavedExcalidrawData = {
  type: "excalidraw";
  version: 2;
  source: string;
  elements: readonly OrderedExcalidrawElement[];
  appState: Partial<AppState>;
  files: BinaryFiles;
};

const isSavedExcalidrawData = (data: unknown): data is SavedExcalidrawData => {
  return (
    !!data &&
    typeof data === "object" &&
    (data as SavedExcalidrawData).type === "excalidraw" &&
    Array.isArray((data as SavedExcalidrawData).elements)
  );
};

const hasValidExcalidrawElementShape = (element: unknown) => {
  if (!element || typeof element !== "object") {
    return false;
  }

  const candidate = element as {
    type?: unknown;
    x?: unknown;
    y?: unknown;
  };

  return (
    typeof candidate.type === "string" &&
    typeof candidate.x === "number" &&
    typeof candidate.y === "number"
  );
};

const validateGeneratedElements = (elements: unknown): unknown[] => {
  if (!Array.isArray(elements) || elements.length === 0) {
    throw new AIBoardGenerationError({
      reason: "missing_elements",
      message: "A resposta da IA veio sem elementos Excalidraw validos.",
    });
  }

  if (!elements.every(hasValidExcalidrawElementShape)) {
    throw new AIBoardGenerationError({
      reason: "missing_elements",
      message: "A resposta da IA veio em um formato invalido.",
    });
  }

  return elements;
};

const getAIGenerationErrorMessage = (error: unknown) => {
  if (error instanceof AIBoardGenerationError) {
    if (error.reason === "missing_config") {
      return "VITE_DRAW_API_URL não está configurada no frontend.";
    }

    if (error.reason === "fetch_failed") {
      return "API indisponível, URL incorreta ou CORS bloqueado. Verifique se http://localhost:4000 está rodando.";
    }

    if (error.reason === "timeout") {
      return "A geração demorou mais que o esperado. Tente novamente com um prompt menor ou use o template local.";
    }

    if (error.reason === "http_error") {
      return `A API respondeu HTTP ${error.status}. Veja o console para o body retornado.`;
    }

    if (error.reason === "invalid_json") {
      return "A API respondeu, mas retornou JSON inválido.";
    }

    if (error.reason === "api_error") {
      return "A API respondeu com erro ao gerar o board.";
    }

    if (error.reason === "missing_elements") {
      return "A API respondeu, mas não retornou elementos Excalidraw válidos.";
    }
  }

  return "Não foi possível gerar com IA real agora.";
};

const GENERATED_BOARD_APPEND_MARGIN = 300;

const getElementsBounds = (
  elements: readonly Pick<
    OrderedExcalidrawElement,
    "x" | "y" | "width" | "height"
  >[],
) => {
  if (!elements.length) {
    return null;
  }

  return elements.reduce(
    (bounds, element) => {
      const minX = Math.min(element.x, element.x + element.width);
      const minY = Math.min(element.y, element.y + element.height);
      const maxX = Math.max(element.x, element.x + element.width);
      const maxY = Math.max(element.y, element.y + element.height);

      return {
        minX: Math.min(bounds.minX, minX),
        minY: Math.min(bounds.minY, minY),
        maxX: Math.max(bounds.maxX, maxX),
        maxY: Math.max(bounds.maxY, maxY),
      };
    },
    {
      minX: Number.POSITIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    },
  );
};

const moveGeneratedElementsBelowCurrentContent = (
  generatedElements: readonly OrderedExcalidrawElement[],
  currentElements: readonly OrderedExcalidrawElement[],
) => {
  const currentBounds = getElementsBounds(currentElements);
  const generatedBounds = getElementsBounds(generatedElements);

  if (!currentBounds || !generatedBounds) {
    return generatedElements;
  }

  const offsetY =
    currentBounds.maxY + GENERATED_BOARD_APPEND_MARGIN - generatedBounds.minY;

  return generatedElements.map((element) =>
    newElementWith(element, {
      y: element.y + offsetY,
    }),
  );
};

const moveElementsToViewportCenter = (
  elements: readonly OrderedExcalidrawElement[],
  appState: AppState,
) => {
  const bounds = getElementsBounds(elements);

  if (!bounds) {
    return elements;
  }

  const zoom = appState.zoom?.value || 1;
  const targetCenterX = (appState.width / 2 - appState.scrollX) / zoom;
  const targetCenterY = (appState.height / 2 - appState.scrollY) / zoom;
  const currentCenterX = bounds.minX + (bounds.maxX - bounds.minX) / 2;
  const currentCenterY = bounds.minY + (bounds.maxY - bounds.minY) / 2;
  const offsetX = targetCenterX - currentCenterX;
  const offsetY = targetCenterY - currentCenterY;

  return elements.map((element) =>
    newElementWith(element, {
      x: element.x + offsetX,
      y: element.y + offsetY,
    }),
  );
};

const ExcalidrawWrapper = ({
  userEmail,
  userId,
  onSignOut,
}: {
  userEmail?: string;
  userId: string;
  onSignOut: () => void;
}) => {
  const excalidrawAPI = useExcalidrawAPI();
  const {
    boards,
    loading: boardsLoading,
    error: boardsError,
    saveNewBoard,
    updateExistingBoard,
    archiveBoard,
  } = useBoards(userId);

  const [errorMessage, setErrorMessage] = useState("");
  const [currentBoardId, setCurrentBoardId] = useState<string | null>(null);
  const [currentBoardTitle, setCurrentBoardTitle] = useState<string | null>(
    null,
  );
  const currentBoardIdRef = useRef<string | null>(null);
  const [isBoardsOpen, setIsBoardsOpen] = useState(false);
  const [isAIHistoryOpen, setIsAIHistoryOpen] = useState(false);
  const [aiGenerations, setAIGenerations] = useState<AIGeneration[]>([]);
  const [aiGenerationsLoading, setAIGenerationsLoading] = useState(false);
  const [aiGenerationsError, setAIGenerationsError] = useState<string | null>(
    null,
  );
  const [isBoardSaving, setIsBoardSaving] = useState(false);
  const [boardMessage, setBoardMessage] = useState("");
  const [isAIGeneratorOpen, setIsAIGeneratorOpen] = useState(false);
  const [aiGeneratorPrompt, setAIGeneratorPrompt] = useState(
    "Crie um funil de vendas para clínica estética",
  );
  const [aiGeneratorType, setAIGeneratorType] =
    useState<MockAITemplateType>("sales-funnel");
  const [aiGenerationMode, setAIGenerationMode] =
    useState<AIGenerationMode>("api");
  const [aiGenerationInsertMode, setAIGenerationInsertMode] =
    useState<AIGenerationInsertMode>("replace");
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [aiGenerationError, setAIGenerationError] = useState<string | null>(
    null,
  );
  const [isDrawLibraryOpen, setIsDrawLibraryOpen] = useState(false);
  const [drawLibraryCatalog, setDrawLibraryCatalog] = useState<
    DrawLibraryCatalogItem[]
  >([]);
  const drawLibraryCatalogRef = useRef<DrawLibraryCatalogItem[]>([]);
  const [drawLibraryCatalogStatus, setDrawLibraryCatalogStatus] =
    useState<DrawLibraryCatalogStatus>("idle");
  const [drawLibraryError, setDrawLibraryError] = useState<string | null>(null);
  const [drawLibraryCategory, setDrawLibraryCategory] = useState(
    DRAW_LIBRARY_CATEGORY_ALL,
  );
  const [drawLibrarySearchQuery, setDrawLibrarySearchQuery] = useState("");
  const [isDrawLibraryInserting, setIsDrawLibraryInserting] = useState(false);
  const [aiUseDrawLibrary, setAIUseDrawLibrary] = useState(false);
  const [aiDrawLibraryCategory, setAIDrawLibraryCategory] = useState(
    DRAW_LIBRARY_CATEGORY_AUTO,
  );
  const isCollabDisabled =
    isRunningInIframe() || !isCollaborationConfigured();

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  const editorInterface = useEditorInterface();

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return !isCollabDisabled && isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);

  const selectCurrentBoard = useCallback(
    (board: Pick<Board, "id" | "title"> | null) => {
      currentBoardIdRef.current = board?.id ?? null;
      setCurrentBoardId(board?.id ?? null);
      setCurrentBoardTitle(board?.title ?? "Sem título");
    },
    [],
  );

  const resetCurrentCanvas = useCallback(() => {
    if (!excalidrawAPI) {
      return;
    }

    excalidrawAPI.resetScene({ resetLoadingState: true });
    selectCurrentBoard(null);
  }, [excalidrawAPI, selectCurrentBoard]);

  const openAIGenerator = useCallback(() => {
    if (excalidrawAPI?.getSceneElements().length) {
      setAIGenerationInsertMode("append-below");
    } else {
      setAIGenerationInsertMode("replace");
    }

    setIsAIGeneratorOpen(true);
  }, [excalidrawAPI]);

  const loadDrawLibraryCatalogData = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (!force && drawLibraryCatalogRef.current.length) {
        return drawLibraryCatalogRef.current;
      }

      setDrawLibraryCatalogStatus("loading");
      setDrawLibraryError(null);

      try {
        const catalog = await loadDrawLibraryCatalog();

        drawLibraryCatalogRef.current = catalog;
        setDrawLibraryCatalog(catalog);
        setDrawLibraryCatalogStatus("ready");
        return catalog;
      } catch (error) {
        const message = getDrawLibraryErrorMessage(error);

        setDrawLibraryCatalogStatus("error");
        setDrawLibraryError(message);
        throw error;
      }
    },
    [],
  );

  const openDrawLibrary = useCallback(() => {
    setIsDrawLibraryOpen(true);
    loadDrawLibraryCatalogData().catch((error) => {
      console.error("[draw-library] catalog load failed", error);
    });
  }, [loadDrawLibraryCatalogData]);

  const handleAIUseDrawLibraryChange = useCallback(
    (useLibrary: boolean) => {
      setAIUseDrawLibrary(useLibrary);
      setAIGenerationError(null);

      if (useLibrary) {
        loadDrawLibraryCatalogData().catch((error) => {
          console.error("[draw-library] catalog load failed", error);
        });
      }
    },
    [loadDrawLibraryCatalogData],
  );

  const handleReloadDrawLibraryCatalog = useCallback(() => {
    loadDrawLibraryCatalogData({ force: true }).catch((error) => {
      console.error("[draw-library] catalog reload failed", error);
    });
  }, [loadDrawLibraryCatalogData]);

  const getDrawLibraryAIRequest = useCallback(async () => {
    if (!aiUseDrawLibrary) {
      return {
        useLibrary: false,
        libraryCategory: undefined,
        libraryCatalog: undefined,
      };
    }

    const catalog = await loadDrawLibraryCatalogData();
    const libraryCatalog = buildLightweightDrawLibraryCatalog({
      catalog,
      category: aiDrawLibraryCategory,
      query: aiGeneratorPrompt,
    });

    if (!libraryCatalog.length) {
      throw new Error("Nenhum item da Biblioteca Draw encontrado.");
    }

    return {
      useLibrary: true,
      libraryCategory: aiDrawLibraryCategory,
      libraryCatalog,
    };
  }, [
    aiDrawLibraryCategory,
    aiGeneratorPrompt,
    aiUseDrawLibrary,
    loadDrawLibraryCatalogData,
  ]);

  const handleInsertDrawLibraryItem = useCallback(
    async (item: DrawLibraryCatalogItem, mode: DrawLibraryInsertMode) => {
      if (!excalidrawAPI || isDrawLibraryInserting) {
        return;
      }

      setIsDrawLibraryInserting(true);
      setDrawLibraryError(null);

      try {
        const { rawLibraryItems, files } = await loadDrawLibraryFile(item);
        const libraryItems = restoreLibraryItems(
          rawLibraryItems as any,
          "published",
        );

        if (!libraryItems.length) {
          throw new Error("Esta biblioteca nao possui itens validos.");
        }

        const distributedElements =
          distributeLibraryItemsOnSquareGrid(libraryItems);
        const restoredElements = restoreElements(distributedElements, null, {
          repairBindings: true,
          deleteInvisibleElements: true,
        });
        const duplicatedElements = duplicateElements({
          type: "everything",
          elements: restoredElements,
          randomizeSeed: true,
        }).duplicatedElements as OrderedExcalidrawElement[];
        const currentElements = excalidrawAPI.getSceneElements();
        const shouldAppendBelow =
          mode === "append-below" && currentElements.length > 0;
        const positionedElements = shouldAppendBelow
          ? moveGeneratedElementsBelowCurrentContent(
              duplicatedElements,
              currentElements,
            )
          : moveElementsToViewportCenter(
              duplicatedElements,
              excalidrawAPI.getAppState(),
            );
        const nextElements =
          mode === "replace"
            ? positionedElements
            : [...currentElements, ...positionedElements];

        excalidrawAPI.updateScene({
          elements: nextElements,
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });

        if (files) {
          excalidrawAPI.addFiles(Object.values(files) as BinaryFileData[]);
        }

        excalidrawAPI.scrollToContent(positionedElements, { animate: true });
        excalidrawAPI.setToast({
          message: "Biblioteca Draw inserida no canvas.",
        });
        setBoardMessage(
          "Biblioteca Draw inserida. Clique em Salvar para persistir.",
        );
        setIsDrawLibraryOpen(false);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : getDrawLibraryErrorMessage(error);

        console.error("[draw-library] insert failed", error);
        setDrawLibraryError(message);
      } finally {
        setIsDrawLibraryInserting(false);
      }
    },
    [excalidrawAPI, isDrawLibraryInserting],
  );

  const refreshAIGenerations = useCallback(async () => {
    setAIGenerationsLoading(true);
    setAIGenerationsError(null);

    try {
      const generations = await getAIGenerations(userId);
      setAIGenerations(generations);
      return generations;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Nao foi possivel carregar o historico de IA.";
      setAIGenerationsError(message);
      throw error;
    } finally {
      setAIGenerationsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refreshAIGenerations().catch((error) => {
      console.error("[ai-generations] failed to load history", error);
    });
  }, [refreshAIGenerations]);

  const getCurrentExcalidrawData = useCallback((): SavedExcalidrawData => {
    if (!excalidrawAPI) {
      throw new Error("Excalidraw ainda nao esta pronto.");
    }

    return {
      type: "excalidraw",
      version: 2,
      source: "draw.agenciafalcone.com.br",
      elements: excalidrawAPI.getSceneElements(),
      appState: clearAppStateForLocalStorage(excalidrawAPI.getAppState()),
      files: excalidrawAPI.getFiles(),
    };
  }, [excalidrawAPI]);

  const handleSaveBoard = useCallback(async () => {
    if (!excalidrawAPI || isBoardSaving) {
      return;
    }

    setBoardMessage("");
    setErrorMessage("");
    setIsBoardSaving(true);

    try {
      const excalidrawData = getCurrentExcalidrawData();
      const activeBoardId = currentBoardIdRef.current;

      if (activeBoardId) {
        const board = await updateExistingBoard({
          boardId: activeBoardId,
          title: currentBoardTitle || undefined,
          excalidrawData,
        });
        selectCurrentBoard(board);
        setBoardMessage("Board atualizado.");
        excalidrawAPI.setToast({ message: "Board atualizado." });
        return;
      }

      const defaultTitle =
        currentBoardTitle && currentBoardTitle !== "Sem título"
          ? currentBoardTitle
          : excalidrawAPI.getName() || "Novo board";
      const title = window.prompt("Nome do board", defaultTitle);

      if (!title?.trim()) {
        setBoardMessage("Salvamento cancelado.");
        return;
      }

      const board = await saveNewBoard({
        title: title.trim(),
        excalidrawData,
      });
      selectCurrentBoard(board);
      setBoardMessage("Board salvo.");
      excalidrawAPI.setToast({ message: "Board salvo." });
    } catch (error: any) {
      const message = error?.message || "Nao foi possivel salvar o board.";
      setErrorMessage(message);
      setBoardMessage(message);
    } finally {
      setIsBoardSaving(false);
    }
  }, [
    currentBoardTitle,
    excalidrawAPI,
    getCurrentExcalidrawData,
    isBoardSaving,
    saveNewBoard,
    selectCurrentBoard,
    updateExistingBoard,
  ]);

  const insertGeneratedBoard = useCallback(
    async ({
      sourceElements,
      response,
      model,
      files,
      generatedTitle,
    }: {
      sourceElements: unknown[];
      response: unknown;
      model: string;
      files?: Record<string, unknown>;
      generatedTitle?: string;
    }) => {
      if (!excalidrawAPI) {
        return;
      }

      const normalizedSourceElements = normalizeGeneratedExcalidrawElements(
        sourceElements as ExcalidrawElementSkeleton[],
      );
      const generatedElements = convertToExcalidrawElements(
        normalizedSourceElements,
        {
          regenerateIds: true,
        },
      );
      const currentElements = excalidrawAPI.getSceneElements();
      const insertionMode =
        currentElements.length || aiGenerationInsertMode !== "append-below"
          ? aiGenerationInsertMode
          : "replace";
      const insertedElements =
        insertionMode === "append-below"
          ? moveGeneratedElementsBelowCurrentContent(
              generatedElements,
              currentElements,
            )
          : generatedElements;
      const nextElements =
        insertionMode === "append-below"
          ? [...currentElements, ...insertedElements]
          : insertedElements;

      excalidrawAPI.updateScene({
        elements: nextElements,
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
      if (files) {
        excalidrawAPI.addFiles(Object.values(files) as BinaryFileData[]);
      }
      if (insertionMode === "new-board") {
        selectCurrentBoard(null);
        setCurrentBoardTitle(generatedTitle?.trim() || "Board gerado com IA");
      }
      excalidrawAPI.scrollToContent(insertedElements, { animate: true });
      excalidrawAPI.setToast({
        message:
          "Board visual gerado no canvas. Clique em Salvar para persistir.",
      });
      setBoardMessage("Board visual gerado. Clique em Salvar para persistir.");
      setIsAIGeneratorOpen(false);

      const responseForHistory =
        response && typeof response === "object" && !Array.isArray(response)
          ? { ...response, insertedElements }
          : { response, insertedElements };

      try {
        await createAIGeneration({
          userId,
          boardId: currentBoardIdRef.current,
          prompt: aiGeneratorPrompt,
          generationType: aiGeneratorType,
          response: responseForHistory,
          model,
          status: "completed",
        });
        await refreshAIGenerations();
      } catch (error: any) {
        const message =
          error?.message || "Nao foi possivel salvar o historico de IA.";
        console.error("[ai-generations] failed to save generation", error);
        setAIGenerationsError(message);
        setBoardMessage(
          "Board visual gerado, mas o historico de IA nao foi salvo.",
        );
      }
    },
    [
      aiGeneratorPrompt,
      aiGeneratorType,
      aiGenerationInsertMode,
      excalidrawAPI,
      refreshAIGenerations,
      selectCurrentBoard,
      userId,
    ],
  );

  const handleGenerateLocalTemplate = useCallback(async () => {
    if (!excalidrawAPI || !aiGeneratorPrompt.trim() || isAIGenerating) {
      return;
    }

    setIsAIGenerating(true);
    setAIGenerationError(null);

    try {
      const skeletonElements = generateExcalidrawTemplate({
        prompt: aiGeneratorPrompt,
        type: aiGeneratorType,
      });

      await insertGeneratedBoard({
        sourceElements: skeletonElements,
        response: {
          type: "mock-local-template",
          elements: skeletonElements,
        },
        model: "mock-local-template",
        generatedTitle: "Template gerado com IA",
      });
    } finally {
      setIsAIGenerating(false);
    }
  }, [
    aiGeneratorPrompt,
    aiGeneratorType,
    excalidrawAPI,
    insertGeneratedBoard,
    isAIGenerating,
  ]);

  const handleGenerateAIBoard = useCallback(async () => {
    if (!excalidrawAPI || !aiGeneratorPrompt.trim() || isAIGenerating) {
      return;
    }

    if (aiGenerationMode === "mock") {
      await handleGenerateLocalTemplate();
      return;
    }

    setIsAIGenerating(true);
    setAIGenerationError(null);

    try {
      let drawLibraryAIRequest;

      try {
        drawLibraryAIRequest = await getDrawLibraryAIRequest();
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : getDrawLibraryErrorMessage(error);

        setAIGenerationError(
          `${message} Desmarque "Usar Biblioteca Draw" para usar a IA normal.`,
        );
        return;
      }

      const apiResponse: GenerateBoardWithAIResponse =
        await generateBoardWithAI({
          prompt: aiGeneratorPrompt,
          generationType: aiGeneratorType,
          currentBoardId: currentBoardIdRef.current,
          userId,
          ...drawLibraryAIRequest,
        });
      const sourceElements = validateGeneratedElements(
        apiResponse.data?.elements,
      );

      await insertGeneratedBoard({
        sourceElements,
        response: {
          ...apiResponse,
          data: {
            ...apiResponse.data,
            elements: sourceElements,
          },
        },
        model: apiResponse.model || "api-draw-ai",
        files: apiResponse.data?.files,
        generatedTitle: apiResponse.data?.title,
      });
    } catch (error) {
      console.error("[ai-generation] real generation failed", error);
      setAIGenerationError(getAIGenerationErrorMessage(error));
    } finally {
      setIsAIGenerating(false);
    }
  }, [
    aiGenerationMode,
    aiGeneratorPrompt,
    aiGeneratorType,
    excalidrawAPI,
    getDrawLibraryAIRequest,
    handleGenerateLocalTemplate,
    insertGeneratedBoard,
    isAIGenerating,
    userId,
  ]);

  const handleOpenBoard = useCallback(
    (board: Board) => {
      if (!excalidrawAPI) {
        return;
      }

      try {
        if (!isSavedExcalidrawData(board.excalidraw_data)) {
          throw new Error("Board sem dados Excalidraw validos.");
        }

        const boardData = board.excalidraw_data;

        excalidrawAPI.updateScene({
          elements: restoreElements(boardData.elements, null, {
            repairBindings: true,
            deleteInvisibleElements: true,
          }),
          appState: restoreAppState(boardData.appState, null),
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });

        if (boardData.files) {
          excalidrawAPI.addFiles(Object.values(boardData.files));
        }

        excalidrawAPI.history.clear();
        selectCurrentBoard(board);
        setIsBoardsOpen(false);
        setBoardMessage(`Board aberto: ${board.title}`);
        excalidrawAPI.setToast({ message: `Board aberto: ${board.title}` });
      } catch (error: any) {
        const message = error?.message || "Nao foi possivel abrir o board.";
        setErrorMessage(message);
        setBoardMessage(message);
      }
    },
    [excalidrawAPI, selectCurrentBoard],
  );

  const handleArchiveBoard = useCallback(
    async (boardId: string) => {
      if (!window.confirm("Tem certeza que deseja arquivar este board?")) {
        return;
      }

      try {
        await archiveBoard(boardId);

        if (currentBoardIdRef.current === boardId) {
          resetCurrentCanvas();
        }

        setBoardMessage("Board arquivado.");
      } catch (error: any) {
        const message = error?.message || "Nao foi possivel arquivar o board.";
        setErrorMessage(message);
        setBoardMessage(message);
      }
    },
    [archiveBoard, resetCurrentCanvas],
  );

  const handleNewBoard = useCallback(() => {
    if (!excalidrawAPI) {
      return;
    }

    const hasSceneData =
      excalidrawAPI.getSceneElements().length > 0 ||
      !!currentBoardIdRef.current;

    if (
      hasSceneData &&
      !window.confirm(
        "Existem alterações no canvas atual. Deseja continuar e criar um novo board?",
      )
    ) {
      return;
    }

    resetCurrentCanvas();
    setBoardMessage("Novo board em branco.");
    excalidrawAPI.setToast({ message: "Novo board em branco." });
  }, [excalidrawAPI, resetCurrentCanvas]);

  const handleRenameBoard = useCallback(async () => {
    if (!excalidrawAPI || isBoardSaving) {
      return;
    }

    const nextTitle = window.prompt(
      "Novo nome do board",
      currentBoardTitle || "Sem título",
    );

    if (!nextTitle?.trim()) {
      setBoardMessage("Renomeação cancelada.");
      return;
    }

    const activeBoardId = currentBoardIdRef.current;

    if (!activeBoardId) {
      setCurrentBoardTitle(nextTitle.trim());
      setBoardMessage("Nome definido. Clique em Salvar para criar o board.");
      return;
    }

    setIsBoardSaving(true);
    setBoardMessage("");
    setErrorMessage("");

    try {
      const board = await updateExistingBoard({
        boardId: activeBoardId,
        title: nextTitle.trim(),
        excalidrawData: getCurrentExcalidrawData(),
      });
      selectCurrentBoard(board);
      setBoardMessage("Board renomeado.");
      excalidrawAPI.setToast({ message: "Board renomeado." });
    } catch (error: any) {
      const message = error?.message || "Nao foi possivel renomear o board.";
      setErrorMessage(message);
      setBoardMessage(message);
    } finally {
      setIsBoardSaving(false);
    }
  }, [
    currentBoardTitle,
    excalidrawAPI,
    getCurrentExcalidrawData,
    isBoardSaving,
    selectCurrentBoard,
    updateExistingBoard,
  ]);

  const handleSaveBoardAs = useCallback(async () => {
    if (!excalidrawAPI || isBoardSaving) {
      return;
    }

    const defaultTitle =
      currentBoardTitle && currentBoardTitle !== "Sem título"
        ? `${currentBoardTitle} copia`
        : excalidrawAPI.getName() || "Novo board";
    const title = window.prompt("Nome da nova cópia", defaultTitle);

    if (!title?.trim()) {
      setBoardMessage("Salvar como cancelado.");
      return;
    }

    setIsBoardSaving(true);
    setBoardMessage("");
    setErrorMessage("");

    try {
      const board = await saveNewBoard({
        title: title.trim(),
        excalidrawData: getCurrentExcalidrawData(),
      });
      selectCurrentBoard(board);
      setBoardMessage("Cópia salva.");
      excalidrawAPI.setToast({ message: "Cópia salva." });
    } catch (error: any) {
      const message = error?.message || "Nao foi possivel salvar a cópia.";
      setErrorMessage(message);
      setBoardMessage(message);
    } finally {
      setIsBoardSaving(false);
    }
  }, [
    currentBoardTitle,
    excalidrawAPI,
    getCurrentExcalidrawData,
    isBoardSaving,
    saveNewBoard,
    selectCurrentBoard,
  ]);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  // ---------------------------------------------------------------------------
  // Hoisted loadImages
  // ---------------------------------------------------------------------------
  const loadImages = useCallback(
    (data: ResolutionType<typeof initializeScene>, isInitialLoad = false) => {
      if (!data.scene || !excalidrawAPI) {
        return;
      }

      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          if (fileIds.length) {
            // Direct Firebase call (not through FileManager), so track manually
            FileStatusStore.updateStatuses(
              fileIds.map((id) => [id, "loading"]),
            );
          }
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
            FileStatusStore.updateStatuses([
              ...loadedFiles.map((f) => [f.id, "loaded"] as [FileId, "loaded"]),
              ...[...erroredFiles.keys()].map(
                (id) => [id, "error"] as [FileId, "error"],
              ),
            ]);
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(async ({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({
            currentFileIds: fileIds,
          });
        }
      }
    },
    [collabAPI, excalidrawAPI],
  );

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    initializeScene({
      collabAPI,
      excalidrawAPI,
      isCollabEnabled: !isCollabDisabled,
      userId,
    }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({
          collabAPI,
          excalidrawAPI,
          isCollabEnabled: !isCollabDisabled,
          userId,
        }).then((data) => {
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              elements: restoreElements(data.scene.elements, null, {
                repairBindings: true,
              }),
              appState: restoreAppState(data.scene.appState, null),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage(userId);
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [
    isCollabDisabled,
    collabAPI,
    excalidrawAPI,
    setLangCode,
    loadImages,
    userId,
  ]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(
        elements,
        appState,
        files,
        () => {
          if (excalidrawAPI) {
            let didChange = false;

            const elements = excalidrawAPI
              .getSceneElementsIncludingDeleted()
              .map((element) => {
                if (
                  LocalData.fileStorage.shouldUpdateImageElementStatus(element)
                ) {
                  const newElement = newElementWith(element, {
                    status: "saved",
                  });
                  if (newElement !== element) {
                    didChange = true;
                  }
                  return newElement;
                }
                return element;
              });

            if (didChange) {
              excalidrawAPI.updateScene({
                elements,
                captureUpdate: CaptureUpdateAction.NEVER,
              });
            }
          }
        },
        userId,
      );
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => {
      if (isCollabDisabled) {
        setErrorMessage(COLLABORATION_UNAVAILABLE_MESSAGE);
        return;
      }

      setShareDialogState({ isOpen: true, type: "collaborationOnly" });
    },
    [isCollabDisabled, setShareDialogState],
  );

  // ---------------------------------------------------------------------------
  // onExport — intercepts file save to wait for pending image loads
  // ---------------------------------------------------------------------------
  const onExport: Required<ExcalidrawProps>["onExport"] = useCallback(
    async function* () {
      let snapshot = FileStatusStore.getSnapshot();
      const { pending, total } = FileStatusStore.getPendingCount(
        snapshot.value,
      );
      if (pending === 0) {
        return;
      }

      // Yield initial progress
      yield {
        type: "progress",
        progress: (total - pending) / total,
        message: `Loading images (${total - pending}/${total})...`,
      };

      // Wait for all pending images to finish
      while (true) {
        snapshot = await FileStatusStore.pull(snapshot.version);
        const { pending: nowPending, total: nowTotal } =
          FileStatusStore.getPendingCount(snapshot.value);

        yield {
          type: "progress",
          progress: (nowTotal - nowPending) / nowTotal,
          message: `Loading images (${nowTotal - nowPending}/${nowTotal})...`,
        };

        if (nowPending === 0) {
          await new Promise((r) => setTimeout(r, 500));
          yield {
            type: "progress",
            message: `Preparing export...`,
          };
          return;
        }
      }
    },
    [],
  );

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  const formatAIGenerationDate = (createdAt: string) => {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(createdAt));
  };

  const getPromptPreview = (prompt: string) => {
    return prompt.length > 72 ? `${prompt.slice(0, 69)}...` : prompt;
  };

  const workspaceMenu = (
    <div className="workspace-menu-section">
      <div className="workspace-menu-current">
        <div className="workspace-menu-current-content">
          <strong>Projeto atual</strong>
          <span title={currentBoardTitle || undefined}>
            Atual: {currentBoardTitle || "Sem título"}
          </span>
          <span className={clsx({ "workspace-menu-danger": boardsError })}>
            Status:{" "}
            {boardsError ||
              (isBoardSaving
                ? "salvando..."
                : boardMessage || "pronto para salvar.")}
          </span>
        </div>
      </div>

      <div className="workspace-menu-group-title">Ações</div>
      <button
        className="dropdown-menu-item-base dropdown-menu-item workspace-menu-item"
        disabled={!excalidrawAPI || isBoardSaving}
        onClick={openAIGenerator}
        type="button"
      >
        <span className="workspace-menu-item-icon">{MagicIcon}</span>
        <span>Gerar com IA</span>
      </button>
      <button
        className="dropdown-menu-item-base dropdown-menu-item workspace-menu-item"
        disabled={!excalidrawAPI || isBoardSaving}
        onClick={handleNewBoard}
        type="button"
      >
        <span className="workspace-menu-item-icon">{PlusIcon}</span>
        <span>Novo board</span>
      </button>
      <button
        className="dropdown-menu-item-base dropdown-menu-item workspace-menu-item"
        disabled={!excalidrawAPI || isBoardSaving}
        onClick={handleSaveBoard}
        type="button"
      >
        <span className="workspace-menu-item-icon">{save}</span>
        <span>{isBoardSaving ? "Salvando..." : "Salvar board"}</span>
      </button>
      <button
        className="dropdown-menu-item-base dropdown-menu-item workspace-menu-item"
        disabled={!excalidrawAPI || isBoardSaving}
        onClick={handleSaveBoardAs}
        type="button"
      >
        <span className="workspace-menu-item-icon">{saveAs}</span>
        <span>Salvar como</span>
      </button>
      <button
        className="dropdown-menu-item-base dropdown-menu-item workspace-menu-item"
        disabled={!excalidrawAPI || isBoardSaving}
        onClick={handleRenameBoard}
        type="button"
      >
        <span className="workspace-menu-item-icon">{pencilIcon}</span>
        <span>Renomear board</span>
      </button>

      <div className="workspace-menu-separator" />
      <div className="workspace-menu-group-title">Boards</div>
      <button
        className="dropdown-menu-item-base dropdown-menu-item workspace-menu-item"
        onClick={() => setIsBoardsOpen((isOpen) => !isOpen)}
        type="button"
      >
        <span className="workspace-menu-item-icon">{LoadIcon}</span>
        <span>Meus boards ({boardsLoading ? "..." : boards.length})</span>
      </button>

      {isBoardsOpen && (
        <div className="workspace-menu-nested workspace-menu-boards">
          {!boardsLoading && boards.length === 0 && (
            <div className="workspace-menu-meta">Nenhum board salvo ainda.</div>
          )}

          {boards.map((board) => (
            <div className="workspace-menu-board" key={board.id}>
              <button
                className={clsx(
                  "dropdown-menu-item-base dropdown-menu-item workspace-menu-board-open",
                  {
                    "dropdown-menu-item--selected": board.id === currentBoardId,
                  },
                )}
                onClick={() => handleOpenBoard(board)}
                title={board.title}
                type="button"
              >
                {board.title}
              </button>
              <button
                className="workspace-menu-archive"
                onClick={() => handleArchiveBoard(board.id)}
                type="button"
              >
                Arquivar
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="workspace-menu-separator" />
      <div className="workspace-menu-group-title">IA</div>
      <button
        className="dropdown-menu-item-base dropdown-menu-item workspace-menu-item"
        onClick={() => {
          setIsAIHistoryOpen((isOpen) => !isOpen);
          if (!isAIHistoryOpen) {
            refreshAIGenerations().catch((error) => {
              console.error(
                "[ai-generations] failed to refresh history",
                error,
              );
            });
          }
        }}
        type="button"
      >
        <span className="workspace-menu-item-icon">{historyIcon}</span>
        <span>
          Histórico de IA ({aiGenerationsLoading ? "..." : aiGenerations.length}
          )
        </span>
      </button>

      {isAIHistoryOpen && (
        <div className="workspace-menu-nested workspace-menu-ai-history">
          <div className="workspace-menu-meta">Últimas gerações</div>
          {aiGenerationsError && (
            <div className="workspace-menu-meta workspace-menu-danger">
              {aiGenerationsError}
            </div>
          )}

          {!aiGenerationsLoading &&
            !aiGenerationsError &&
            aiGenerations.length === 0 && (
              <div className="workspace-menu-meta">
                Nenhuma geração registrada ainda.
              </div>
            )}

          {aiGenerations.map((generation) => (
            <div className="workspace-menu-ai-history-item" key={generation.id}>
              <div className="workspace-menu-ai-history-row">
                <strong>{generation.generation_type}</strong>
                <span>{formatAIGenerationDate(generation.created_at)}</span>
              </div>
              <p title={generation.prompt}>
                {getPromptPreview(generation.prompt)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const accountMenu = (
    <div className="workspace-account-section">
      <span className="workspace-menu-meta" title={userEmail}>
        {userEmail}
      </span>
      <button
        className="dropdown-menu-item-base dropdown-menu-item workspace-menu-item"
        onClick={onSignOut}
        type="button"
      >
        <span className="workspace-menu-item-icon">{loginIcon}</span>
        <span>Sair</span>
      </button>
    </div>
  );

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <Excalidraw
        onChange={onChange}
        onExport={onExport}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        aiEnabled={false}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopRightUI={(isMobile) => {
          if (isMobile || !collabAPI || isCollabDisabled) {
            return null;
          }

          return (
            <div className="excalidraw-ui-top-right">
              {collabError.message && <CollabError collabError={collabError} />}
              <LiveCollaborationTrigger
                isCollaborating={isCollaborating}
                onSelect={() =>
                  setShareDialogState({ isOpen: true, type: "share" })
                }
                editorInterface={editorInterface}
              />
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
          workspace={workspaceMenu}
          account={accountMenu}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}
        {isAIGeneratorOpen && (
          <AIMockGeneratorDialog
            prompt={aiGeneratorPrompt}
            type={aiGeneratorType}
            mode={aiGenerationMode}
            insertMode={aiGenerationInsertMode}
            isGenerating={isAIGenerating}
            error={aiGenerationError}
            useDrawLibrary={aiUseDrawLibrary}
            drawLibraryCategory={aiDrawLibraryCategory}
            drawLibraryCategories={DRAW_LIBRARY_AI_CATEGORY_OPTIONS}
            drawLibraryStatus={drawLibraryCatalogStatus}
            drawLibraryError={aiUseDrawLibrary ? drawLibraryError : null}
            onPromptChange={setAIGeneratorPrompt}
            onTypeChange={setAIGeneratorType}
            onModeChange={(mode) => {
              setAIGenerationMode(mode);
              setAIGenerationError(null);
            }}
            onInsertModeChange={setAIGenerationInsertMode}
            onUseDrawLibraryChange={handleAIUseDrawLibraryChange}
            onDrawLibraryCategoryChange={setAIDrawLibraryCategory}
            onGenerate={handleGenerateAIBoard}
            onUseLocalFallback={handleGenerateLocalTemplate}
            onClose={() => setIsAIGeneratorOpen(false)}
          />
        )}

        {isDrawLibraryOpen && (
          <DrawLibraryDialog
            catalog={drawLibraryCatalog}
            categories={DRAW_LIBRARY_MANUAL_CATEGORY_OPTIONS}
            selectedCategory={drawLibraryCategory}
            searchQuery={drawLibrarySearchQuery}
            status={drawLibraryCatalogStatus}
            error={drawLibraryError}
            isInserting={isDrawLibraryInserting}
            onCategoryChange={setDrawLibraryCategory}
            onSearchChange={setDrawLibrarySearchQuery}
            onInsert={handleInsertDrawLibraryItem}
            onRetry={handleReloadDrawLibraryCatalog}
            onClose={() => setIsDrawLibraryOpen(false)}
          />
        )}

        <DrawTopMenuActions
          disabled={!excalidrawAPI || isBoardSaving}
          onGenerateAI={openAIGenerator}
          onOpenLibrary={openDrawLibrary}
        />
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        <ShareDialog
          collabAPI={isCollabDisabled ? null : collabAPI}
          onExportToBackend={async () => {
            if (excalidrawAPI) {
              try {
                await onExportToBackend(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !isCollabDisabled && !!collabAPI,
              keywords: [
                "team",
                "multiplayer",
                "share",
                "public",
                "session",
                "invite",
              ],
              icon: usersIcon,
              perform: () => {
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () =>
                !isCollabDisabled && !!collabAPI?.isCollaborating(),
              keywords: [
                "stop",
                "session",
                "end",
                "leave",
                "close",
                "exit",
                "collaboration",
              ],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            {
              ...CommandPalette.defaultItems.toggleTheme,
              perform: () => {
                setAppTheme(
                  editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                );
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Excalidraw>
    </div>
  );
};

const AuthenticatedExcalidrawApp = () => {
  const { user, loading, signIn, signUp, signOut } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#4f4b61",
          fontFamily:
            "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        Carregando...
      </div>
    );
  }

  if (!user) {
    return <AuthPage loading={loading} signIn={signIn} signUp={signUp} />;
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawAPIProvider>
          <ExcalidrawWrapper
            key={user.id}
            onSignOut={() => {
              signOut().catch((error) => {
                console.error("[auth] sign out failed", error);
              });
            }}
            userEmail={user.email}
            userId={user.id}
          />
        </ExcalidrawAPIProvider>
      </Provider>
    </TopErrorBoundary>
  );
};

const ExcalidrawApp = () => {
  return <AuthenticatedExcalidrawApp />;
};

export default ExcalidrawApp;
